import React, { useState, useEffect } from 'react';
import { validateOTP } from '../../utils/otpValidation';

export default function ExternalUSTransfer({ accounts, banks, onTransfer }) {
  const [fromAccount, setFromAccount] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [transferData, setTransferData] = useState(null);
  const [routingNumber, setRoutingNumber] = useState('');
  const [showLockModal, setShowLockModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [transferDetails, setTransferDetails] = useState(null);

  useEffect(() => {
    if (selectedBank) {
      const bank = banks.find(b => b.name === selectedBank);
      setRoutingNumber(bank ? bank.routingNumber : '');
    } else {
      setRoutingNumber('');
    }
  }, [selectedBank, banks]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const validateForm = () => {
    if (!fromAccount || !selectedBank || !accountNumber || !accountName || !amount || !reference) {
      setError('Please fill in all fields');
      return false;
    }

    const transferAmount = parseFloat(amount);
    if (isNaN(transferAmount) || transferAmount <= 0) {
      setError('Please enter a valid amount');
      return false;
    }

    if (accountNumber.length < 8 || accountNumber.length > 17) {
      setError('Please enter a valid account number');
      return false;
    }

    const sourceAccount = accounts.find(acc => acc.type === fromAccount);
    if (transferAmount > sourceAccount.available) {
      setError('Insufficient funds');
      return false;
    }

    return true;
  };

  const handleInitialSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    const selectedBankDetails = banks.find(bank => bank.name === selectedBank);
    
    setTransferData({
      fromAccount,
      toBank: selectedBank,
      routingNumber: selectedBankDetails.routingNumber,
      accountNumber,
      accountName,
      amount: parseFloat(amount),
      reference,
      transferType: 'external'
    });

    setShowOtpInput(true);
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!otp) {
      setError('Please enter OTP');
      return;
    }

    if (!validateOTP(otp)) {
      setError('Invalid OTP');
      return;
    }

    setLoading(true);
    try {
      await onTransfer(transferData);
      
      setTransferDetails({
        amount: transferData.amount,
        accountName: transferData.accountName,
        bank: transferData.toBank,
        reference: transferData.reference
      });
      
      setShowSuccessModal(true);

      setTimeout(() => {
        setFromAccount('');
        setSelectedBank('');
        setAccountNumber('');
        setAccountName('');
        setAmount('');
        setReference('');
        setOtp('');
        setShowOtpInput(false);
        setTransferData(null);
        setShowSuccessModal(false);
      }, 3000);

    } catch (error) {
      if (error.message === 'ACCOUNT_LOCKED') {
        setShowLockModal(true);
        setError('');
      } else {
        setError('Transfer failed. Please try again.');
      }
    }
    setLoading(false);
  };

  if (showOtpInput) {
    return (
      <div className="max-w-2xl mx-auto">
        {showLockModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full mx-auto p-6 space-y-4 relative animate-fadeIn">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-red-100 rounded-full p-3">
                  <svg className="h-8 w-8 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-center text-gray-900">
                Transaction Denied
              </h3>
              
              <p className="text-gray-600 text-center">
                For your security, external transfers have been temporarily suspended. 
                Please visit your local branch or contact our support team for assistance.
              </p>

              <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600">
                <p className="font-medium text-gray-900 mb-1">Contact Support</p>
                <p>🕒 Monday - Friday: 8AM - 8PM EST</p>
              </div>
                
              <button
                onClick={() => {
                  setShowLockModal(false);
                  setShowOtpInput(false);
                }}
                className="w-full py-3 px-4 border border-transparent rounded-xl shadow-sm text-white 
                  bg-primary-600 hover:bg-primary-700 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 
                  transform transition-all duration-200"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {showSuccessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full mx-auto p-6 space-y-4 relative animate-fadeIn">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-green-100 rounded-full p-3">
                  <svg className="h-8 w-8 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-center text-gray-900">
                Transfer Successful!
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-semibold text-gray-900">
                    ${transferDetails?.amount.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">To:</span>
                  <span className="font-semibold text-gray-900">
                    {transferDetails?.accountName}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Bank:</span>
                  <span className="font-semibold text-gray-900">
                    {transferDetails?.bank}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Reference:</span>
                  <span className="font-semibold text-gray-900">
                    {transferDetails?.reference}
                  </span>
                </div>
              </div>

              <p className="text-sm text-center text-gray-600">
                Your transfer has been processed successfully.
                A confirmation email will be sent shortly.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleOtpSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-gray-50 p-4 rounded-xl space-y-3">
            <h3 className="text-sm font-medium text-gray-700">Transfer Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">To:</span>
                <span className="text-sm font-medium text-gray-900">{accountName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Bank:</span>
                <span className="text-sm font-medium text-gray-900">{selectedBank}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Amount:</span>
                <span className="text-sm font-medium text-gray-900">${parseFloat(amount).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Enter OTP
            </label>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter 6-digit OTP"
              className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
              disabled={loading}
              maxLength="6"
              pattern="\d{6}"
            />
          </div>

          <div className="pt-4 flex space-x-4">
            <button
              type="button"
              onClick={() => setShowOtpInput(false)}
              className="flex-1 py-3 px-4 border border-gray-200 rounded-xl shadow-sm text-gray-700 
                bg-white hover:bg-gray-50 
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
                disabled:bg-gray-100 disabled:cursor-not-allowed
                transition-colors duration-200"
              disabled={loading}
            >
              Back
            </button>
            <button
              type="submit"
              className="flex-1 flex justify-center py-3 px-4 border border-transparent rounded-xl 
                shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
                disabled:bg-gray-300 disabled:cursor-not-allowed
                transition-colors duration-200"
              disabled={loading}
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                'Confirm Transfer'
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <form onSubmit={handleInitialSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              From Account
            </label>
            <select
              value={fromAccount}
              onChange={(e) => setFromAccount(e.target.value)}
              className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
              disabled={loading}
            >
              <option value="">Select account</option>
              {accounts.map((account) => (
                <option key={account.accountNumber} value={account.type}>
                  {account.type} - {formatCurrency(account.available)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Select Bank
            </label>
            <select
              value={selectedBank}
              onChange={(e) => setSelectedBank(e.target.value)}
              className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
              disabled={loading}
            >
              <option value="">Select bank</option>
              {banks.map((bank) => (
                <option key={bank.routingNumber} value={bank.name}>
                  {bank.name}
                </option>
              ))}
            </select>
            {routingNumber && (
              <p className="mt-1 text-sm text-gray-500">
                Routing Number: {routingNumber}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Account Number
            </label>
            <input
              type="text"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter account number"
              className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
              maxLength="17"
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Account Holder Name
            </label>
            <input
              type="text"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="Enter account holder name"
              className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
              disabled={loading}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Amount
          </label>
          <div className="relative mt-1 rounded-xl shadow-sm">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
              <span className="text-gray-500 sm:text-sm">$</span>
            </div>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="block w-full pl-8 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
              disabled={loading}
              min="0.01"
              step="0.01"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Reference
          </label>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="What's this transfer for?"
            className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
              focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
              hover:bg-white transition-colors duration-200
              disabled:bg-gray-100 disabled:cursor-not-allowed
              text-sm"
            disabled={loading}
          />
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl 
              shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
              disabled:bg-gray-300 disabled:cursor-not-allowed
              transition-colors duration-200"
          >
            {loading ? (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      </form>
    </div>
  );
} 